<template>
  <div>
    <div class="page-header">
      <div>
        <h5 class="page-title">Cases</h5>
        <p class="page-sub">Track and resolve customer support cases</p>
      </div>
      <div class="header-actions">
        <div class="export-wrap" ref="exportWrap">
          <button class="btn-export" @click="exportMenuOpen = !exportMenuOpen" :disabled="exporting">
            <font-awesome-icon icon="download" /> Export <font-awesome-icon icon="chevron-down" style="font-size:10px" />
          </button>
          <div v-if="exportMenuOpen" class="export-menu">
            <button class="export-opt" @click="openExportModal('csv')"><font-awesome-icon icon="file-csv" class="me-2" />Export CSV</button>
            <button class="export-opt" @click="openExportModal('pdf')"><font-awesome-icon icon="file-pdf" class="me-2" />Export PDF</button>
          </div>
        </div>
        <button v-if="permission?.create_cases" class="btn-primary" @click="openCreate">
          <font-awesome-icon icon="plus" class="me-1" /> New Case
        </button>
      </div>
    </div>

    <div class="filter-bar">
      <div class="per-page-wrap">
        <label class="per-page-label">Show</label>
        <select class="per-page-select" v-model.number="length" @change="page = 1; fetchCases()">
          <option v-for="n in [5,10,20,50,100,200,500,1000]" :key="n" :value="n">{{ n }}</option>
        </select>
        <span class="per-page-label">per page</span>
      </div>
      <input v-model="search" @input="debouncedFetch" class="filter-input" placeholder="Search cases..." />
      <select v-model="filterStatus" @change="page=1; fetchCases()" class="filter-select">
        <option value="">All Statuses</option>
        <option value="open">Open</option>
        <option value="in_progress">In Progress</option>
        <option value="resolved">Resolved</option>
        <option value="closed">Closed</option>
      </select>
      <select v-model="filterPriority" @change="page=1; fetchCases()" class="filter-select">
        <option value="">All Priorities</option>
        <option value="low">Low</option>
        <option value="medium">Medium</option>
        <option value="high">High</option>
        <option value="critical">Critical</option>
      </select>
      <input type="date" v-model="dateFrom" class="filter-input" style="max-width:160px" />
      <input type="date" v-model="dateTo" class="filter-input" style="max-width:160px" />
      <button class="btn-apply" @click="applyDateFilter">Apply</button>
      <button class="btn-clear" @click="clearDateFilter" v-if="dateFrom || dateTo">Clear</button>
    </div>

    <div class="card">
      <div v-if="loading" class="loading-state"><div class="spinner"></div><p>Loading cases...</p></div>
      <div v-else-if="!cases.length" class="empty-state">
        <font-awesome-icon icon="ticket-alt" class="empty-icon" />
        <p>No cases found</p>
        <span>Cases track support requests from customers</span>
      </div>
      <table v-else class="data-table">
        <thead>
          <tr><th>Case #</th><th>Subject</th><th>Customer</th><th>Priority</th><th>Status</th><th>Assigned To</th><th>Created</th><th></th></tr>
        </thead>
        <tbody>
          <tr v-for="c in cases" :key="c.id">
            <td class="mono">{{ c.case_number }}</td>
            <td class="name-col">{{ c.subject }}</td>
            <td>{{ c.customer?.customer_name ?? '—' }}</td>
            <td><span :class="['priority-badge', `priority--${c.priority}`]">{{ c.priority }}</span></td>
            <td><span :class="['status-badge', `status--${c.status}`]">{{ fmtStatus(c.status) }}</span></td>
            <td>{{ c.assignee ? c.assignee.first_name + ' ' + c.assignee.last_name : '—' }}</td>
            <td>{{ fmtDate(c.created_at) }}</td>
            <td>
              <div class="row-actions">
                <button class="btn-link btn-link--view" @click="openDetail(c)">View</button>
                <button v-if="permission?.edit_cases && ['open','in_progress'].includes(c.status)" class="btn-link btn-link--resolve" @click="resolveCase(c)">Resolve</button>
                <button v-if="permission?.edit_cases" class="btn-link btn-link--edit" @click="openEdit(c)">Edit</button>
                <button v-if="permission?.delete_cases" class="btn-link btn-link--del" @click="confirmDelete(c)">Delete</button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      <div v-if="meta" class="pagination-bar">
        <span class="pagination-info">{{ meta.from ?? 1 }}–{{ meta.to ?? meta.total }} of {{ meta.total }} records</span>
        <div class="pagination-controls">
          <button class="pg-btn" :disabled="meta.current_page <= 1" @click="changePage(1)">«</button>
          <button class="pg-btn" :disabled="meta.current_page <= 1" @click="changePage(meta.current_page - 1)">‹</button>
          <div class="pg-pages">
            <button v-for="p in pageRange" :key="p" class="pg-num" :class="{ 'pg-num--active': p === meta.current_page }" @click="changePage(p)">{{ p }}</button>
          </div>
          <button class="pg-btn" :disabled="meta.current_page >= meta.last_page" @click="changePage(meta.current_page + 1)">›</button>
          <button class="pg-btn" :disabled="meta.current_page >= meta.last_page" @click="changePage(meta.last_page)">»</button>
        </div>
      </div>
    </div>

    <div class="modal-overlay" :class="{ open: modalOpen }" @click.self="closeModal">
      <div class="modal-box modal-box--form">
        <div class="modal-header">
          <span class="modal-title">{{ editing ? 'Edit Case' : 'New Case' }}</span>
          <button class="modal-close" @click="closeModal"><font-awesome-icon icon="times" /></button>
        </div>
        <form @submit.prevent="submit">
          <div class="modal-body">
            <div class="form-group form-group--full">
              <label class="form-label">Customer <span class="req">*</span></label>
              <Multiselect
                v-model="form.customer_id"
                :options="customerOptions"
                :searchable="true"
                :filter-results="true"
                placeholder="Search customer..."
                value-prop="value"
                label="label"
                class="ms-case"
              />
            </div>
            <div class="form-group form-group--full">
              <label class="form-label">Subject <span class="req">*</span></label>
              <input v-model="form.subject" class="form-input" required />
            </div>
            <div class="form-row">
              <div class="form-group">
                <label class="form-label">Priority</label>
                <select v-model="form.priority" class="form-input">
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </div>
              <div class="form-group" v-if="editing">
                <label class="form-label">Status</label>
                <select v-model="form.status" class="form-input">
                  <option value="open">Open</option>
                  <option value="in_progress">In Progress</option>
                  <option value="resolved">Resolved</option>
                  <option value="closed">Closed</option>
                </select>
              </div>
            </div>
            <div class="form-group form-group--full">
              <label class="form-label">Assigned To</label>
              <Multiselect
                v-model="form.assigned_to"
                :options="employeeOptions"
                :searchable="true"
                :filter-results="true"
                placeholder="Search employee..."
                value-prop="value"
                label="label"
                class="ms-case"
              />
            </div>
            <div class="form-group form-group--full">
              <label class="form-label">Description</label>
              <textarea v-model="form.description" class="form-input" rows="3"></textarea>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn-ghost" @click="closeModal">Cancel</button>
            <button type="submit" class="btn-primary" :disabled="submitting">{{ submitting ? 'Saving…' : (editing ? 'Save Changes' : 'Create Case') }}</button>
          </div>
        </form>
      </div>
    </div>

    <div class="modal-overlay" :class="{ open: !!deleteTarget }" @click.self="deleteTarget = null">
      <div class="modal-box modal-box--sm">
        <div class="modal-header"><span class="modal-title">Delete Case</span><button class="modal-close" @click="deleteTarget = null"><font-awesome-icon icon="times" /></button></div>
        <div class="modal-body"><p>Delete case <strong>{{ deleteTarget?.case_number }}</strong>? This cannot be undone.</p></div>
        <div class="modal-footer">
          <button class="btn-ghost" @click="deleteTarget = null">Cancel</button>
          <button class="btn-danger" @click="doDelete" :disabled="submitting">{{ submitting ? 'Deleting…' : 'Delete' }}</button>
        </div>
      </div>
    </div>

    <!-- Resolve Case Confirm -->
    <div class="modal-overlay" :class="{ open: !!resolveTarget }" @click.self="resolveTarget = null">
      <div class="modal-box modal-box--sm">
        <div class="modal-header">
          <div class="modal-header-icon"><font-awesome-icon icon="check-circle" /></div>
          <div>
            <span class="modal-title">Resolve Case</span>
            <p class="modal-sub">Mark this case as resolved</p>
          </div>
          <button class="modal-close" @click="resolveTarget = null"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">
          <div class="action-card">
            <p class="action-meta mono">{{ resolveTarget?.case_number }}</p>
            <p class="action-name">{{ resolveTarget?.subject }}</p>
            <p class="action-meta" v-if="resolveTarget?.priority">Priority: <strong>{{ resolveTarget.priority }}</strong></p>
          </div>
          <p class="action-note">The case status will be set to <strong>Resolved</strong>. You can still reopen it by editing the case.</p>
        </div>
        <div class="modal-footer">
          <button class="btn-ghost" @click="resolveTarget = null">Cancel</button>
          <button class="btn-resolve" @click="doResolve" :disabled="submitting">
            <font-awesome-icon icon="check" class="me-1" />
            {{ submitting ? 'Resolving…' : 'Resolve Case' }}
          </button>
        </div>
      </div>
    </div>
    <!-- Case Detail Drawer -->
    <transition name="drawer">
      <div v-if="detailOpen" class="drawer-overlay" @click.self="closeDetail">
        <div class="detail-drawer">
          <div class="detail-header">
            <div class="detail-header-left">
              <span class="mono detail-case-num">{{ detailCase?.case_number }}</span>
              <span v-if="detailCase" :class="['status-badge', `status--${detailCase.status}`]">{{ fmtStatus(detailCase.status) }}</span>
              <span v-if="detailCase" :class="['priority-badge', `priority--${detailCase.priority}`]">{{ detailCase.priority }}</span>
            </div>
            <button class="modal-close" @click="closeDetail"><font-awesome-icon icon="times" /></button>
          </div>

          <div v-if="detailLoading" class="loading-state"><div class="spinner"></div></div>
          <template v-else-if="detailCase">
            <div class="detail-body">
              <h3 class="detail-subject">{{ detailCase.subject }}</h3>

              <div class="detail-grid">
                <div class="detail-item">
                  <span class="detail-label">Customer</span>
                  <span class="detail-val">{{ detailCase.customer?.customer_name ?? '—' }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Assigned To</span>
                  <span class="detail-val">{{ detailCase.assignee ? detailCase.assignee.first_name + ' ' + detailCase.assignee.last_name : '—' }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Type</span>
                  <span class="detail-val">{{ fmtStatus(detailCase.type) }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Channel</span>
                  <span class="detail-val">{{ fmtStatus(detailCase.channel) }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">SLA Due</span>
                  <span class="detail-val" :class="{ 'sla-breached': detailCase.sla_breached }">
                    {{ detailCase.sla_due_at ? fmtDateTime(detailCase.sla_due_at) : '—' }}
                    <span v-if="detailCase.sla_breached" class="sla-tag">Breached</span>
                  </span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Created</span>
                  <span class="detail-val">{{ fmtDateTime(detailCase.created_at) }}</span>
                </div>
                <div v-if="detailCase.resolved_at" class="detail-item">
                  <span class="detail-label">Resolved</span>
                  <span class="detail-val">{{ fmtDateTime(detailCase.resolved_at) }}</span>
                </div>
              </div>

              <div v-if="detailCase.description" class="detail-section">
                <p class="detail-section-title">Description</p>
                <p class="detail-text">{{ detailCase.description }}</p>
              </div>

              <div v-if="detailCase.resolution_notes" class="detail-section">
                <p class="detail-section-title">Resolution Notes</p>
                <p class="detail-text">{{ detailCase.resolution_notes }}</p>
              </div>

              <!-- Comments -->
              <div class="detail-section">
                <p class="detail-section-title">Comments ({{ detailCase.comments?.length ?? 0 }})</p>
                <div v-if="!detailCase.comments?.length" class="comments-empty">No comments yet.</div>
                <div v-else class="comments-list">
                  <div v-for="cm in detailCase.comments" :key="cm.id" class="comment-item">
                    <div class="comment-avatar">{{ commentInitials(cm) }}</div>
                    <div class="comment-body">
                      <div class="comment-meta">
                        <span class="comment-author">{{ cm.author ? cm.author.first_name + ' ' + cm.author.last_name : 'Unknown' }}</span>
                        <span class="comment-time">{{ fmtDateTime(cm.created_at) }}</span>
                      </div>
                      <p class="comment-text">{{ cm.body }}</p>
                    </div>
                  </div>
                </div>

                <div class="comment-compose">
                  <textarea
                    v-model="newComment"
                    class="comment-input"
                    placeholder="Add a comment..."
                    rows="2"
                    @keydown.ctrl.enter="submitComment"
                  ></textarea>
                  <div class="comment-compose-footer">
                    <span class="comment-hint">Ctrl+Enter to submit</span>
                    <button class="btn-primary" :disabled="!newComment.trim() || commentSubmitting" @click="submitComment">
                      {{ commentSubmitting ? 'Posting…' : 'Post Comment' }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </transition>
    <!-- Column-Picker Export Modal -->
    <transition name="fade">
      <div v-if="showExportModal" class="col-picker-backdrop" @click.self="showExportModal=false">
        <div class="col-picker-modal">
          <div class="col-picker-header">
            <span>Choose Columns to Export <span class="col-picker-badge">{{ selectedExportCols.length }}</span></span>
            <button class="col-picker-close" @click="showExportModal=false"><font-awesome-icon icon="times"/></button>
          </div>
          <div class="col-picker-body">
            <div class="col-picker-actions">
              <button class="col-picker-link" @click="selectedExportCols=exportColumnDefs.map(c=>c.key)">Select All</button>
              <button class="col-picker-link" @click="selectedExportCols=[]">Deselect All</button>
            </div>
            <div class="col-picker-grid">
              <label v-for="col in exportColumnDefs" :key="col.key" class="col-picker-item">
                <input type="checkbox" :value="col.key" v-model="selectedExportCols" />
                {{ col.label }}
              </label>
            </div>
          </div>
          <div class="col-picker-footer">
            <button class="btn-ghost" @click="showExportModal=false">Cancel</button>
            <button class="btn-primary" :disabled="!selectedExportCols.length||exporting" @click="doExport">
              <font-awesome-icon v-if="exporting" icon="spinner" spin class="me-1"/>
              Export {{ pendingExportFmt.toUpperCase() }}
            </button>
          </div>
        </div>
      </div>
    </transition>

  </div>
</template>

<script>
import axios from 'axios';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { useToast } from 'vue-toastification';
import { useStore } from 'vuex';
import Multiselect from '@vueform/multiselect';
export default {
  components: { Multiselect },
  setup() { return { toast: useToast(), store: useStore() }; },
  computed: {
    permission() { return this.store.state.getCurrentUserPermission; },
    pageRange() {
      if (!this.meta) return [];
      const cur = this.meta.current_page, last = this.meta.last_page;
      const range = [];
      for (let i = Math.max(1, cur - 2); i <= Math.min(last, cur + 2); i++) range.push(i);
      return range;
    },
    customerOptions() {
      return this.customers.map(c => ({ value: c.id, label: c.customer_name }));
    },
    employeeOptions() {
      return this.employees.map(e => ({ value: e.id, label: `${e.first_name} ${e.last_name}` }));
    },
  },
  data() {
    return {
      cases: [], meta: null, loading: false, submitting: false,
      search: '', filterStatus: '', filterPriority: '', page: 1, length: 5,
      dateFrom: '', dateTo: '', exportMenuOpen: false, exporting: false,
      showExportModal: false, pendingExportFmt: 'csv', selectedExportCols: [],
      exportColumnDefs: [
        { key: 'case_number', label: 'Case #',      pdfWidth: 80  },
        { key: 'subject',     label: 'Subject',     pdfWidth: 180 },
        { key: 'customer',    label: 'Customer',    pdfWidth: 130 },
        { key: 'priority',    label: 'Priority',    pdfWidth: 70  },
        { key: 'status',      label: 'Status',      pdfWidth: 90  },
        { key: 'assignee',    label: 'Assigned To', pdfWidth: 120 },
        { key: 'created_at',  label: 'Created',     pdfWidth: 91  },
      ],
      modalOpen: false, editing: null, deleteTarget: null, resolveTarget: null,
      form: { subject:'', priority:'medium', status:'open', description:'', customer_id: null, assigned_to: null },
      customers: [], employees: [],
      debounceTimer: null,
      detailOpen: false, detailCase: null, detailLoading: false,
      newComment: '', commentSubmitting: false,
    };
  },
  mounted() { this.fetchCases(); this.fetchCustomers(); this.fetchEmployees(); document.addEventListener('click', this.onOutsideClick); },
  beforeUnmount() { document.removeEventListener('click', this.onOutsideClick); },
  methods: {
    async fetchCases() {
      this.loading = true;
      try {
        const { data } = await axios.get('/crm/cases', { params: { search: this.search, status: this.filterStatus, priority: this.filterPriority, from: this.dateFrom || undefined, to: this.dateTo || undefined, page: this.page, length: this.length } });
        this.cases = data.data?.data ?? data.data ?? data ?? [];
        this.meta = data.data?.meta ?? data.meta ?? null;
      } catch { this.toast.error('Failed to load cases'); }
      finally { this.loading = false; }
    },
    debouncedFetch() { clearTimeout(this.debounceTimer); this.debounceTimer = setTimeout(() => { this.page = 1; this.fetchCases(); }, 400); },
    changePage(p) { this.page = p; this.fetchCases(); },
    applyDateFilter() { this.page = 1; this.fetchCases(); },
    clearDateFilter() { this.dateFrom = ''; this.dateTo = ''; this.page = 1; this.fetchCases(); },
    openExportModal(format) {
      this.exportMenuOpen     = false;
      this.pendingExportFmt   = format;
      this.selectedExportCols = this.exportColumnDefs.map(c => c.key);
      this.showExportModal    = true;
    },
    getRowValue(r, key) {
      switch (key) {
        case 'case_number': return r.case_number ?? '—';
        case 'subject':     return r.subject ?? '—';
        case 'customer':    return r.customer?.customer_name ?? '—';
        case 'priority':    return r.priority ?? '—';
        case 'status':      return r.status ?? '—';
        case 'assignee':    return r.assignee ? ((r.assignee.first_name ?? '') + ' ' + (r.assignee.last_name ?? '')).trim() : '—';
        case 'created_at':  return r.created_at ? new Date(r.created_at).toLocaleDateString('en-GB') : '—';
        default:            return '—';
      }
    },
    async doExport() {
      if (!this.selectedExportCols.length) return;
      this.exporting = true; this.showExportModal = false;
      try {
        const params = { page: 1, per_page: 10000 };
        if (this.search)         params.search   = this.search;
        if (this.filterStatus)   params.status   = this.filterStatus;
        if (this.filterPriority) params.priority = this.filterPriority;
        if (this.dateFrom)       params.from     = this.dateFrom;
        if (this.dateTo)         params.to       = this.dateTo;
        const { data } = await axios.get('/crm/cases', { params });
        const rows = data.data?.data ?? data.data ?? data ?? [];
        const cols = this.exportColumnDefs.filter(c => this.selectedExportCols.includes(c.key));
        const headers = cols.map(c => c.label);
        const body    = rows.map(r => cols.map(c => this.getRowValue(r, c.key)));
        const date    = new Date().toISOString().slice(0, 10);
        if (this.pendingExportFmt === 'csv') {
          const csv  = [headers, ...body].map(r => r.map(v => `"${String(v).replace(/"/g,'""')}"`).join(',')).join('\n');
          const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
          const url  = URL.createObjectURL(blob);
          const a    = document.createElement('a');
          a.href = url; a.download = `cases-${date}.csv`; a.click(); URL.revokeObjectURL(url);
        } else {
          const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
          const usable = 761; const totalW = cols.reduce((s,c)=>s+c.pdfWidth,0); const scale = usable/totalW;
          const colStyles = {}; cols.forEach((c,i)=>{ colStyles[i]={cellWidth:c.pdfWidth*scale}; });
          doc.setFontSize(18); doc.setFont('helvetica','bold'); doc.setTextColor(26,43,69); doc.text('Cases',40,46);
          doc.setFontSize(9); doc.setFont('helvetica','normal'); doc.setTextColor(122,144,176);
          doc.text(`Exported ${new Date().toLocaleDateString('en-GB')}   ·   ${rows.length} record(s)`,40,62);
          doc.autoTable({ startY:78, head:[headers], body,
            styles:{fontSize:7.5,cellPadding:3.5,textColor:[26,43,69],overflow:'ellipsize'},
            headStyles:{fillColor:[0,74,173],textColor:255,fontStyle:'bold',fontSize:7.5},
            alternateRowStyles:{fillColor:[248,250,252]}, columnStyles:colStyles, tableWidth:usable, margin:{left:40,right:40},
            didDrawPage(d){ doc.setFontSize(8); doc.setTextColor(192,202,214); doc.text(`Page ${d.pageNumber}`,doc.internal.pageSize.width/2,doc.internal.pageSize.height-16,{align:'center'}); },
          });
          doc.save(`cases-${date}.pdf`);
        }
      } catch { this.toast?.error('Export failed. Please try again.'); }
      finally { this.exporting = false; }
    },
    onOutsideClick(e) {
      if (this.$refs.exportWrap && !this.$refs.exportWrap.contains(e.target)) this.exportMenuOpen = false;
    },
    async fetchCustomers() {
      try {
        const { data } = await axios.get('/crm/accounts', { params: { per_page: 500 } });
        this.customers = data.data?.data ?? data.data ?? [];
      } catch { /* silent */ }
    },
    async fetchEmployees() {
      try {
        const { data } = await axios.get('/payroll/employees/active');
        this.employees = data.data ?? data ?? [];
      } catch { /* silent */ }
    },
    openCreate() {
      this.editing = null;
      this.form = { subject:'', priority:'medium', status:'open', description:'', customer_id: null, assigned_to: null };
      this.modalOpen = true;
    },
    openEdit(c) {
      this.editing = c.id;
      this.form = {
        subject: c.subject, priority: c.priority, status: c.status,
        description: c.description ?? '',
        customer_id: c.customer_id ?? null,
        assigned_to: c.assigned_to ?? null,
      };
      this.modalOpen = true;
    },
    closeModal() { this.modalOpen = false; },
    confirmDelete(c) { this.deleteTarget = c; },
    async submit() {
      this.submitting = true;
      try {
        if (this.editing) { await axios.put(`/crm/cases/${this.editing}`, this.form); this.toast.success('Case updated'); }
        else              { await axios.post('/crm/cases', this.form); this.toast.success('Case created'); }
        this.closeModal(); this.fetchCases();
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed to save'); }
      finally { this.submitting = false; }
    },
    async doDelete() {
      this.submitting = true;
      try { await axios.delete(`/crm/cases/${this.deleteTarget.id}`); this.toast.success('Deleted'); this.deleteTarget = null; this.fetchCases(); }
      catch { this.toast.error('Failed to delete'); }
      finally { this.submitting = false; }
    },
    resolveCase(c) { this.resolveTarget = c; },
    async doResolve() {
      this.submitting = true;
      try {
        await axios.post(`/crm/cases/${this.resolveTarget.id}/resolve`);
        this.toast.success('Case resolved');
        this.resolveTarget = null;
        this.fetchCases();
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed to resolve'); }
      finally { this.submitting = false; }
    },
    async openDetail(c) {
      this.detailCase = c;
      this.detailOpen = true;
      this.newComment = '';
      this.detailLoading = true;
      try {
        const { data } = await axios.get(`/crm/cases/${c.id}`);
        this.detailCase = data.data ?? data;
      } catch { this.toast.error('Failed to load case details'); }
      finally { this.detailLoading = false; }
    },
    closeDetail() { this.detailOpen = false; this.detailCase = null; },
    async submitComment() {
      if (!this.newComment.trim() || this.commentSubmitting) return;
      this.commentSubmitting = true;
      try {
        const { data } = await axios.post(`/crm/cases/${this.detailCase.id}/comments`, { body: this.newComment.trim() });
        if (!this.detailCase.comments) this.detailCase.comments = [];
        this.detailCase.comments.push(data.data);
        this.newComment = '';
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed to post comment'); }
      finally { this.commentSubmitting = false; }
    },
    commentInitials(cm) {
      if (!cm.author) return '?';
      return (cm.author.first_name?.[0] ?? '') + (cm.author.last_name?.[0] ?? '');
    },
    fmtStatus(s) { return s?.replace(/_/g,' ').replace(/\b\w/g, c => c.toUpperCase()) ?? s; },
    fmtDate(v) { return new Date(v).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }); },
    fmtDateTime(v) { return new Date(v).toLocaleString('en-GB', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' }); },
  }
};
</script>

<style scoped>
.page-header{display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:20px}
.page-title{font-size:20px;font-weight:700;color:#1a2b45;margin:0 0 3px}
.page-sub{font-size:13px;color:#7a90b0;margin:0}
.filter-bar{display:flex;gap:10px;margin-bottom:16px;flex-wrap:wrap}
.filter-input{padding:9px 13px;border:1.5px solid #dde5f0;border-radius:8px;font-size:13px;color:#1a2b45;flex:1;min-width:180px;outline:none}
.filter-input:focus{border-color:#004AAD}
.filter-select{padding:9px 12px;border:1.5px solid #dde5f0;border-radius:8px;font-size:13px;color:#1a2b45;background:#fff;outline:none}
.btn-primary{background:#004AAD;color:#fff;border:none;padding:9px 18px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer;display:inline-flex;align-items:center;gap:6px}
.btn-primary:hover{background:#003b8e}
.btn-primary:disabled{opacity:.55;cursor:not-allowed}
.btn-ghost{background:#f4f7fb;color:#3d5068;border:1px solid #e4eaf2;padding:8px 16px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer}
.btn-danger{background:#e03535;color:#fff;border:none;padding:8px 16px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer}
.btn-danger:disabled{opacity:.55;cursor:not-allowed}
.card{background:#fff;border-radius:12px;border:1.5px solid #e8edf5;overflow:hidden}
.loading-state,.empty-state{display:flex;flex-direction:column;align-items:center;justify-content:center;padding:56px 24px;color:#7a90b0;gap:8px}
.empty-icon{font-size:36px;opacity:.4}
.spinner{width:28px;height:28px;border:3px solid #e8edf5;border-top-color:#004AAD;border-radius:50%;animation:spin .7s linear infinite}
@keyframes spin{to{transform:rotate(360deg)}}
.data-table{width:100%;border-collapse:collapse;font-size:13px}
.data-table th{background:#f4f7fb;font-weight:600;color:#5a6a85;padding:11px 14px;text-align:left;white-space:nowrap}
.data-table td{padding:11px 14px;border-top:1px solid #f0f4fa;color:#1a2b45;vertical-align:middle}
.data-table tbody tr:hover{background:#f9fbff}
.name-col{font-weight:600}
.mono{font-family:monospace;font-size:12px;color:#7a90b0}
.row-actions{display:flex;gap:8px}
.btn-link{background:none;border:none;font-size:12.5px;cursor:pointer;padding:0;font-weight:600}
.btn-link--edit{color:#004AAD}
.btn-link--resolve{color:#166534}
.btn-link--del{color:#e03535}
.status-badge,.priority-badge{display:inline-block;font-size:11px;font-weight:700;border-radius:20px;padding:2px 9px;text-transform:capitalize}
.status--open{background:#e0f2fe;color:#075985}
.status--in_progress{background:#fef3c7;color:#92400e}
.status--resolved{background:#dcfce7;color:#166534}
.status--closed{background:#f3f4f6;color:#6b7280}
.priority--low{background:#f3f4f6;color:#374151}
.priority--medium{background:#fef3c7;color:#92400e}
.priority--high{background:#ffedd5;color:#9a3412}
.priority--critical{background:#fee2e2;color:#991b1b}
.per-page-wrap{display:flex;align-items:center;gap:8px;flex-shrink:0}
.per-page-label{font-size:13px;color:#5a6a85;white-space:nowrap}
.per-page-select{padding:7px 10px;border:1.5px solid #dde5f0;border-radius:8px;font-size:13px;color:#1a2b45;background:#fff;outline:none;cursor:pointer}
.per-page-select:focus{border-color:#004AAD}
.pagination-bar{display:flex;align-items:center;justify-content:space-between;padding:12px 16px;border-top:1px solid #f0f4fa;flex-wrap:wrap;gap:10px}
.pagination-info{font-size:12.5px;color:#7a90b0}
.pagination-controls{display:flex;align-items:center;gap:4px}
.pg-btn{padding:6px 10px;border:1.5px solid #dde5f0;border-radius:7px;background:#fff;color:#1a2b45;font-size:13px;cursor:pointer}
.pg-btn:disabled{opacity:.4;cursor:not-allowed}
.pg-pages{display:flex;gap:3px}
.pg-num{width:30px;height:30px;border:1.5px solid #dde5f0;border-radius:7px;background:#fff;color:#1a2b45;font-size:12.5px;cursor:pointer;display:flex;align-items:center;justify-content:center}
.pg-num:hover{border-color:#004AAD;color:#004AAD}
.pg-num--active{background:#004AAD;border-color:#004AAD;color:#fff}
.modal-overlay{position:fixed;inset:0;z-index:1050;display:flex;align-items:center;justify-content:center;background:rgba(15,25,50,.45);backdrop-filter:blur(3px);opacity:0;pointer-events:none;transition:opacity .2s;padding:16px}
.modal-overlay.open{opacity:1;pointer-events:all}
.modal-box{background:#fff;border-radius:14px;width:100%;max-width:560px;box-shadow:0 20px 60px rgba(0,0,0,.14);animation:slideUp .2s ease;max-height:90vh;overflow-y:auto}
.modal-box--sm{max-width:420px}
@keyframes slideUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}
.modal-header{display:flex;align-items:center;justify-content:space-between;padding:18px 22px;border-bottom:1px solid #e4eaf2}
.modal-title{font-size:16px;font-weight:700;color:#1a2b45}
.modal-close{width:28px;height:28px;border-radius:7px;border:1px solid #dde5f0;background:transparent;cursor:pointer;font-size:14px;color:#7a90b0;display:flex;align-items:center;justify-content:center}
.modal-body{padding:20px 22px}
.modal-footer{display:flex;justify-content:flex-end;gap:10px;padding:16px 22px;border-top:1px solid #f0f4fa}
.form-row{display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-bottom:4px}
.form-group{display:flex;flex-direction:column;gap:5px;margin-bottom:14px}
.form-group--full{grid-column:1/-1}
.form-label{font-size:12.5px;font-weight:600;color:#1a2b45}
.form-input{padding:9px 12px;border:1.5px solid #dde5f0;border-radius:8px;font-size:13px;color:#1a2b45;outline:none;width:100%;font-family:inherit}
.form-input:focus{border-color:#004AAD;box-shadow:0 0 0 3px rgba(0,74,173,.07)}
.req{color:#e03535}
.me-1{margin-right:4px}
.modal-header-icon{width:36px;height:36px;border-radius:9px;background:#dcfce7;color:#166534;display:flex;align-items:center;justify-content:center;font-size:16px;flex-shrink:0}
.modal-sub{font-size:12px;color:#7a90b0;margin:2px 0 0}
.action-card{background:#f4f7fb;border-radius:10px;padding:14px 16px;margin-bottom:14px}
.action-name{font-size:14px;font-weight:700;color:#1a2b45;margin:4px 0 0}
.action-meta{font-size:13px;color:#7a90b0;margin:0 0 2px}
.action-note{font-size:13px;color:#5a6a85;margin:0}
.btn-resolve{background:#166534;color:#fff;border:none;padding:8px 18px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer;display:inline-flex;align-items:center;gap:6px}
.btn-resolve:hover{background:#14532d}
.btn-resolve:disabled{opacity:.55;cursor:not-allowed}
.modal-box--form{overflow:visible}
.modal-box--form .modal-body{overflow-y:auto;max-height:calc(90vh - 130px)}
.ms-case{--ms-border-color:#dde5f0;--ms-border-width:1.5px;--ms-radius:8px;--ms-font-size:13px;--ms-line-height:1.5;--ms-option-font-size:13px;--ms-py:9px;--ms-px:12px;--ms-ring-color:rgba(0,74,173,.07);--ms-ring-width:3px;--ms-dropdown-bg:#fff;--ms-dropdown-border-color:#dde5f0;--ms-dropdown-radius:8px;--ms-option-bg-pointed:#f4f7fb;--ms-option-bg-selected:#004AAD;--ms-option-color-selected:#fff}
.btn-link--view{color:#7a90b0}
/* Drawer */
.drawer-overlay{position:fixed;inset:0;z-index:1060;background:rgba(15,25,50,.35);display:flex;justify-content:flex-end}
.detail-drawer{width:520px;max-width:100vw;background:#fff;height:100%;display:flex;flex-direction:column;box-shadow:-4px 0 32px rgba(0,0,0,.12);overflow:hidden}
.detail-header{display:flex;align-items:center;justify-content:space-between;padding:16px 20px;border-bottom:1px solid #e4eaf2;flex-shrink:0;gap:10px}
.detail-header-left{display:flex;align-items:center;gap:8px;flex-wrap:wrap}
.detail-case-num{font-size:13px;font-weight:700;color:#7a90b0}
.detail-body{flex:1;overflow-y:auto;padding:20px}
.detail-subject{font-size:17px;font-weight:700;color:#1a2b45;margin:0 0 16px}
.detail-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:18px}
.detail-item{display:flex;flex-direction:column;gap:3px}
.detail-label{font-size:11px;font-weight:700;color:#7a90b0;text-transform:uppercase;letter-spacing:.04em}
.detail-val{font-size:13px;color:#1a2b45;font-weight:500}
.sla-breached{color:#e03535}
.sla-tag{display:inline-block;background:#fee2e2;color:#991b1b;font-size:10px;font-weight:700;border-radius:10px;padding:1px 7px;margin-left:4px}
.detail-section{margin-top:18px;padding-top:16px;border-top:1px solid #f0f4fa}
.detail-section-title{font-size:12px;font-weight:700;color:#7a90b0;text-transform:uppercase;letter-spacing:.04em;margin:0 0 10px}
.detail-text{font-size:13.5px;color:#1a2b45;line-height:1.6;margin:0;white-space:pre-wrap}
.comments-empty{font-size:13px;color:#b0beca;padding:8px 0}
.comments-list{display:flex;flex-direction:column;gap:14px;margin-bottom:16px}
.comment-item{display:flex;gap:10px}
.comment-avatar{width:32px;height:32px;border-radius:50%;background:#e0e9f7;color:#004AAD;font-size:11px;font-weight:700;display:flex;align-items:center;justify-content:center;flex-shrink:0;text-transform:uppercase}
.comment-body{flex:1}
.comment-meta{display:flex;align-items:baseline;gap:8px;margin-bottom:3px}
.comment-author{font-size:12.5px;font-weight:700;color:#1a2b45}
.comment-time{font-size:11px;color:#b0beca}
.comment-text{font-size:13px;color:#3d5068;line-height:1.55;margin:0;white-space:pre-wrap}
.comment-compose{margin-top:12px}
.comment-input{width:100%;padding:10px 12px;border:1.5px solid #dde5f0;border-radius:8px;font-size:13px;color:#1a2b45;resize:vertical;font-family:inherit;outline:none;box-sizing:border-box}
.comment-input:focus{border-color:#004AAD;box-shadow:0 0 0 3px rgba(0,74,173,.07)}
.comment-compose-footer{display:flex;align-items:center;justify-content:space-between;margin-top:8px}
.comment-hint{font-size:11px;color:#b0beca}
/* Drawer transition */
.drawer-enter-active,.drawer-leave-active{transition:transform .22s ease}
.drawer-enter-from,.drawer-leave-to{transform:translateX(100%)}
.header-actions{display:flex;align-items:center;gap:10px}
.export-wrap{position:relative}
.btn-export{background:#fff;color:#1a2b45;border:1.5px solid #dde5f0;padding:8px 15px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer;display:inline-flex;align-items:center;gap:6px}
.btn-export:hover{border-color:#004AAD;color:#004AAD}
.btn-export:disabled{opacity:.55;cursor:not-allowed}
.export-menu{position:absolute;right:0;top:calc(100% + 4px);background:#fff;border:1.5px solid #e4eaf2;border-radius:9px;box-shadow:0 8px 28px rgba(0,0,0,.1);z-index:200;min-width:140px;padding:5px 0}
.export-opt{display:block;width:100%;background:none;border:none;padding:9px 16px;text-align:left;font-size:13px;color:#1a2b45;cursor:pointer}
.export-opt:hover{background:#f4f7fb}
.btn-apply{background:#004AAD;color:#fff;border:none;padding:9px 15px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer}
.btn-apply:hover{background:#003b8e}
.btn-clear{background:#f4f7fb;color:#3d5068;border:1px solid #e4eaf2;padding:9px 15px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer}
.me-2{margin-right:6px}
.col-picker-backdrop{position:fixed;inset:0;background:rgba(10,20,40,.45);z-index:1100;display:flex;align-items:center;justify-content:center}
.col-picker-modal{background:#fff;border-radius:14px;width:480px;max-width:95vw;box-shadow:0 8px 40px rgba(0,74,173,.18);overflow:hidden}
.col-picker-header{display:flex;align-items:center;justify-content:space-between;padding:18px 22px 14px;border-bottom:1.5px solid #eaeff6;font-size:15px;font-weight:700;color:#1a2b45}
.col-picker-badge{background:#004AAD;color:#fff;border-radius:20px;padding:1px 9px;font-size:12px;font-weight:700;margin-left:8px}
.col-picker-close{background:none;border:none;cursor:pointer;color:#7a90b0;font-size:16px;line-height:1}
.col-picker-body{padding:16px 22px}
.col-picker-actions{display:flex;gap:14px;margin-bottom:12px}
.col-picker-link{background:none;border:none;color:#004AAD;font-size:13px;font-weight:600;cursor:pointer;padding:0}
.col-picker-link:hover{text-decoration:underline}
.col-picker-grid{display:grid;grid-template-columns:1fr 1fr;gap:8px 16px}
.col-picker-item{display:flex;align-items:center;gap:8px;font-size:13.5px;color:#1a2b45;cursor:pointer}
.col-picker-item input[type=checkbox]{width:15px;height:15px;accent-color:#004AAD;cursor:pointer}
.col-picker-footer{display:flex;justify-content:flex-end;gap:10px;padding:14px 22px 18px;border-top:1.5px solid #eaeff6}
.fade-enter-active,.fade-leave-active{transition:opacity .18s ease}
.fade-enter,.fade-leave-to{opacity:0}
</style>
